import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { OnboardingLayout } from './OnboardingLayout';
import { onboardingPath } from './onboardingRoutes';
import { AppInfoTooltip } from '../../components/AppInfoTooltip';
import { AppLogo } from './AppLogo';
import { Chevron, COLLAPSE_APPS_ABOVE } from './AppsDisclosure';
import { appBenefit, outcomeBenefits } from './appBenefits';

interface UnlockedApp {
  appId: string;
  displayName: string;
  // Phase 2.x-c — Application Registry v2 metadata, mode-resolved on the
  // backend. Optional so the client degrades gracefully if PR-A hasn't
  // shipped yet or the entry lacks copy.
  subtitle?: string;
  description?: string;
  /** The app's logo — the same file the app switcher shows. */
  iconUrl?: string;
}

interface Outcome {
  code: string;
  displayName: string;
  description: string;
  icon: string;
  apps: string[];
  /** Apps this outcome unlocks, in order — the source of the card's benefits. */
  unlockedApps?: UnlockedApp[];
  category: 'personal' | 'business' | 'productivity';
  pricing: {
    includedCredits: number;
    trialDays: number;
  };
}

interface OnboardingState {
  step: string;
  version: number;
  onboardingMode: 'business' | 'personal' | null;
  workspaceType: 'personal' | 'team' | 'enterprise' | null;
  workspacePurpose: 'personal' | 'business' | 'client' | null;
  selectedOutcomes: string[];
  isComplete: boolean;
}

type OutcomeMode = 'business' | 'personal';

/** Mode-native copy — no abstract "select outcomes" language. */
const MODE_COPY: Record<OutcomeMode, { heading: string; sub: string }> = {
  business: {
    heading: 'Choose what you want your workspace to help you improve.',
    sub: 'Pick the outcomes that matter — we assemble the apps and workflows for each.',
  },
  personal: {
    heading: 'Choose what you want your space to help you organize or strengthen.',
    sub: 'Pick what matters to you — we set up the apps and tools for each.',
  },
};

// SVG icons for outcomes (mapped from Lucide icon names)
const OUTCOME_ICONS: Record<string, JSX.Element> = {
  home: (
    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  ),
  users: (
    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ),
  'message-circle': (
    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  ),
  'trending-up': (
    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  ),
  briefcase: (
    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  calendar: (
    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  'graduation-cap': (
    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 14l9-5-9-5-9 5 9 5z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
    </svg>
  ),
  shield: (
    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  ),
};

const CATEGORY_LABELS: Record<string, string> = {
  personal: 'Personal',
  business: 'Business',
  productivity: 'Productivity',
};

/**
 * OutcomeSelection page — the onboarding outcomes step.
 *
 * Outcomes are mode-scoped (R2): a business workspace sees business +
 * productivity outcomes; a personal workspace sees personal + productivity
 * outcomes. The list is fetched with `?mode=` so personal-life outcomes
 * never appear in the business path, and vice versa. Each card lists, in
 * plain words, what the outcome will let you do — never the names of the
 * apps behind it; the side panel introduces those names as small secondary
 * labels. If no outcomes resolve, a recoverable error is shown — never a
 * silent empty screen.
 */
export function OutcomeSelectionPage() {
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);
  const [selectedOutcomes, setSelectedOutcomes] = useState<string[]>([]);
  const [state, setState] = useState<OnboardingState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  // The loader must not re-run when the navigate function's identity changes.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const continueUrl = searchParams.get('continue') || '';
  // The mode the URL names (/onboarding/{mode}/outcomes); null on a legacy URL.
  const urlMode: OutcomeMode | null = pathname.startsWith('/onboarding/personal/')
    ? 'personal'
    : pathname.startsWith('/onboarding/business/')
      ? 'business'
      : null;

  // Load onboarding state, then the mode-scoped outcome list.
  //
  // THE WORKSPACE'S STORED MODE DECIDES THE PAGE. The server validates the
  // submission against it, so a page showing the other mode's cards can only
  // end in "Outcomes not available in … mode" with no way forward. Hence:
  //  - a URL in the other mode is redirected to the stored mode's route;
  //  - a state that cannot be read is an error, never a "business" default;
  //  - only selections this page actually shows are kept (a mode switch can
  //    leave the other mode's codes in the stored selection, and a card that
  //    is not shown cannot be unticked).
  // The stored selection is exactly what the user ticked (every tick is
  // saved — see saveSelection). Nothing is pre-ticked for them: falling back
  // to "recommended" outcomes ticked most cards whenever nothing was saved.
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setLoadError('');
    try {
      const stateRes = await fetch('/api/onboarding/state', { credentials: 'include' });
      const stateData = await stateRes.json().catch(() => null);

      if (!stateRes.ok || !stateData?.state) {
        setLoadError("We couldn't load your options. Please try again.");
        setOutcomes([]);
        return;
      }

      const stored = stateData.state.onboardingMode;
      const storedMode: OutcomeMode | null =
        stored === 'personal' || stored === 'business' ? stored : null;
      if (storedMode && urlMode && storedMode !== urlMode) {
        navigateRef.current(onboardingPath(storedMode, 'outcomes', continueUrl), { replace: true });
        return;
      }
      // Legacy workspaces have no stored mode: the URL's mode, else business.
      const mode: OutcomeMode = storedMode ?? urlMode ?? 'business';
      setState({ ...stateData.state, onboardingMode: mode });

      // Mode-scoped outcomes — business/personal never see each other's.
      const outcomesRes = await fetch(`/api/focuses?mode=${mode}`, { credentials: 'include' });
      const outcomesData = await outcomesRes.json();

      const resolved: Outcome[] =
        outcomesRes.ok && Array.isArray(outcomesData.outcomes) ? outcomesData.outcomes : [];
      if (resolved.length === 0) {
        // Never a silent empty screen — surface a recoverable error.
        setLoadError("We couldn't load your options. Please try again.");
        setOutcomes([]);
        return;
      }
      setOutcomes(resolved);

      const shown = new Set(resolved.map((o) => o.code));
      const saved: string[] = Array.isArray(stateData.state.selectedOutcomes)
        ? stateData.state.selectedOutcomes
        : [];
      setSelectedOutcomes(saved.filter((code) => shown.has(code)));
    } catch {
      setLoadError("We couldn't load your options. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [urlMode, continueUrl]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const mode: OutcomeMode = state?.onboardingMode === 'personal' ? 'personal' : 'business';

  // Group outcomes by category
  const outcomesByCategory = useMemo(() => {
    const grouped: Record<string, Outcome[]> = {};
    for (const outcome of outcomes) {
      if (!grouped[outcome.category]) {
        grouped[outcome.category] = [];
      }
      grouped[outcome.category].push(outcome);
    }
    return grouped;
  }, [outcomes]);

  // Apps preview from the canonical visibility helper. Refetches on every
  // outcome toggle (onboarding is single-user low-volume). Phase 2.x-c
  // PR-B: each app carries the Application Registry v2 subtitle +
  // mode-resolved description straight from /api/onboarding/app-preview
  // — no client-side join, no auxiliary metadata map.
  const [selectedApps, setSelectedApps] = useState<
    Array<{
      appId: string;
      displayName: string;
      subtitle?: string;
      description?: string;
      iconUrl?: string;
      order: number;
    }>
  >([]);
  // A long list in the side panel starts with only its first few rows.
  const [appsExpanded, setAppsExpanded] = useState(false);

  useEffect(() => {
    if (selectedOutcomes.length === 0) {
      setSelectedApps([]);
      return;
    }
    // Only the answer for the CURRENT ticks may land: quick ticks race, and
    // an older answer arriving last showed apps for a selection that no
    // longer exists (or none at all).
    let current = true;
    const params = new URLSearchParams({ outcomes: selectedOutcomes.join(',') });
    fetch(`/api/onboarding/app-preview?${params.toString()}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((data) => {
        if (!current) return;
        // `prospectiveApps` = apps completion WILL activate for this
        // selection (backend canonical projection). `apps` is the
        // deprecated alias older backends send.
        const prospective = Array.isArray(data?.prospectiveApps)
          ? data.prospectiveApps
          : data?.apps;
        if (data?.ok && Array.isArray(prospective)) {
          setSelectedApps(prospective);
        } else {
          setSelectedApps([]);
        }
      })
      .catch(() => {
        // Non-blocking — preview stays at the prior state.
      });
    return () => {
      current = false;
    };
  }, [selectedOutcomes]);

  // Every tick is saved, so leaving this screen — Back, the browser's back
  // button, a reload — and returning shows exactly what was ticked. Saves run
  // one at a time, and a save that starts late sends only the newest ticks,
  // so an older selection can never land after a newer one.
  const pendingSelection = useRef<string[] | null>(null);
  const selectionSaved = useRef<Promise<void>>(Promise.resolve());
  const saveSelection = (codes: string[]) => {
    pendingSelection.current = codes;
    selectionSaved.current = selectionSaved.current.then(async () => {
      const outcomes = pendingSelection.current;
      if (!outcomes) return;
      pendingSelection.current = null;
      try {
        await fetch('/api/onboarding/selected-outcomes', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ outcomes }),
        });
      } catch {
        // Best effort — Continue still sends the full selection.
      }
    });
  };

  const toggleOutcome = (code: string) => {
    const next = selectedOutcomes.includes(code)
      ? selectedOutcomes.filter((c) => c !== code)
      : [...selectedOutcomes, code];
    setSelectedOutcomes(next);
    saveSelection(next);
  };

  const handleBack = async () => {
    await selectionSaved.current;
    try {
      await fetch('/api/onboarding/back', {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Ignore errors on back navigation
    }

    // The step before outcomes is the mode's setup screen (R7: the route
    // is mode-specific — /onboarding/{mode}/setup).
    navigate(onboardingPath(mode, 'setup', continueUrl));
  };

  const handleContinue = async () => {
    if (selectedOutcomes.length === 0) return;

    setIsSaving(true);
    setError('');

    try {
      // A tick still being saved must not land after Continue's write.
      await selectionSaved.current;
      const response = await fetch('/api/onboarding/step', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ step: 'outcomes', data: { outcomes: selectedOutcomes } }),
      });

      const data = await response.json();

      if (!response.ok) {
        // The workspace is in the OTHER mode now (switched after this page
        // loaded — another tab, or Back/Forward to a stale page). Re-enter
        // through /start/{mode}, which resumes the workspace where it really
        // is, instead of leaving the user on an error they cannot act on.
        const refusedMode = data?.details?.mode;
        if (
          data?.code === 'INVALID_OUTCOMES' &&
          (refusedMode === 'personal' || refusedMode === 'business') &&
          refusedMode !== mode
        ) {
          const suffix = continueUrl ? `?continue=${encodeURIComponent(continueUrl)}` : '';
          navigate(`/start/${refusedMode}${suffix}`, { replace: true });
          return;
        }
        setError(data.error || 'Failed to save outcomes');
        return;
      }

      navigate(onboardingPath(mode, 'pricing', continueUrl));
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <OnboardingLayout currentStep="outcomes" workspaceType={state?.workspaceType} onboardingMode={mode} wide>
        <div className="bg-white shadow-2xl shadow-black/40 ring-1 ring-black/5 rounded-2xl p-8 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading your options...</p>
        </div>
      </OnboardingLayout>
    );
  }

  // Recoverable error — never a silent empty outcomes screen.
  if (loadError) {
    return (
      <OnboardingLayout currentStep="outcomes" workspaceType={state?.workspaceType} onboardingMode={mode} wide>
        <div className="bg-white shadow-2xl shadow-black/40 ring-1 ring-black/5 rounded-2xl p-8 text-center max-w-md mx-auto">
          <h1 className="text-lg font-semibold text-gray-900">Something went wrong</h1>
          <p className="text-sm text-gray-500 mt-2">{loadError}</p>
          <button
            type="button"
            onClick={() => loadData()}
            className="mt-6 px-6 py-2 bg-emerald-600 text-white rounded-lg shadow-lg shadow-emerald-600/25 font-medium hover:bg-emerald-700 transition-colors"
          >
            Try again
          </button>
        </div>
      </OnboardingLayout>
    );
  }

  // The side panel shows its first rows and folds the rest behind "Show N
  // more". It only folds two or more rows: a toggle that revealed a single
  // row would cost the row it saves.
  const appsCollapsible = selectedApps.length > COLLAPSE_APPS_ABOVE + 1;
  const shownApps =
    appsCollapsible && !appsExpanded ? selectedApps.slice(0, COLLAPSE_APPS_ABOVE) : selectedApps;

  return (
    <OnboardingLayout currentStep="outcomes" workspaceType={state?.workspaceType} onboardingMode={mode} wide>
      <div className="bg-white shadow-2xl shadow-black/40 ring-1 ring-black/5 rounded-3xl p-3.5 sm:p-8">
        <div className="mb-4 text-center sm:mb-7">
          <h1 className="text-xl sm:text-3xl font-semibold tracking-tight text-gray-900">
            {MODE_COPY[mode].heading}
          </h1>
          <p className="mx-auto mt-1 max-w-2xl text-sm text-gray-500 sm:mt-2 sm:text-base">{MODE_COPY[mode].sub}</p>
        </div>

        <div className="grid lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
          {/* LEFT: Outcome cards grouped by category */}
          <div className="lg:col-span-3">
            {Object.entries(outcomesByCategory).map(([category, categoryOutcomes]) => (
              <div key={category} className="mb-5 last:mb-0 sm:mb-8">
                <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700 sm:mb-3 sm:text-xs">
                  {CATEGORY_LABELS[category] || category}
                </h2>
                <div className="grid gap-2.5 sm:gap-4 grid-cols-1 sm:grid-cols-2">
                  {categoryOutcomes.map((outcome) => {
                    const isSelected = selectedOutcomes.includes(outcome.code);
                    // What picking this lets you do, in plain words — never
                    // the names of the apps behind it.
                    const benefits = outcomeBenefits(
                      outcome.unlockedApps?.length
                        ? outcome.unlockedApps
                        : (outcome.apps ?? []).map((appId) => ({ appId })),
                      mode,
                    );
                    return (
                      <div
                        key={outcome.code}
                        className={`group relative rounded-2xl border transition-all duration-200 ${
                          isSelected
                            ? 'border-emerald-500 bg-emerald-50/60 shadow-md ring-2 ring-emerald-500/25'
                            : 'border-gray-200 bg-white hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-md'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => toggleOutcome(outcome.code)}
                          aria-pressed={isSelected}
                          className="relative block w-full rounded-2xl p-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-400 sm:p-5"
                        >
                          {!isSelected && (
                            <span
                              aria-hidden="true"
                              className="absolute right-3 top-3 h-5 w-5 rounded-full border-2 border-gray-200 transition-colors group-hover:border-emerald-300"
                            />
                          )}
                          {isSelected && (
                            <svg
                              className="w-5 h-5 text-emerald-600 absolute top-3 right-3"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                          <div className="flex items-start gap-3 sm:gap-4">
                            <div
                              className={`flex h-9 w-9 sm:h-12 sm:w-12 flex-shrink-0 items-center justify-center rounded-xl transition-colors ${
                                isSelected
                                  ? 'bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-lg shadow-emerald-600/25'
                                  : 'bg-gray-100 text-gray-500 group-hover:bg-emerald-50 group-hover:text-emerald-600'
                              }`}
                            >
                              {OUTCOME_ICONS[outcome.icon] || OUTCOME_ICONS.briefcase}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="pr-6 text-sm sm:text-base font-semibold text-gray-900">
                                {outcome.displayName}
                              </h3>
                              <p className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-gray-500 sm:mt-1 sm:line-clamp-none sm:text-sm">
                                {outcome.description}
                              </p>
                              {/*
                                What picking this lets you do: up to three plain
                                benefits of its apps — no app names, logos or
                                "Unlocks" row. Inside the card's button, so a tap
                                anywhere on the card picks it, as before.
                              */}
                              {benefits.length > 0 && (
                                <ul
                                  className="mt-2 space-y-1 sm:mt-3 sm:space-y-1.5"
                                  data-testid={`outcome-benefits-${outcome.code}`}
                                >
                                  {benefits.map((benefit) => (
                                    <li
                                      key={benefit}
                                      className="flex items-start gap-1.5 text-xs leading-snug text-gray-700 sm:gap-2 sm:text-[13px]"
                                    >
                                      <svg
                                        className={`mt-px h-3.5 w-3.5 flex-none sm:h-4 sm:w-4 ${
                                          isSelected ? 'text-emerald-600' : 'text-emerald-500'
                                        }`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                      >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                      </svg>
                                      <span>{benefit}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </div>
                        </button>
                        {/*
                          Phase 2.x-c PR-C3a — per-outcome $ badges retired.
                          Outcomes contribute $0 today, so "Included" or
                          "$0/mo" per-outcome implied outcome pricing that
                          doesn't exist. Plan price + capacity live on
                          PricingSummary.
                        */}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/*
            RIGHT: what the picks will set up. Each row leads with the plain
            benefit; the app's small logo and name sit under it as a quiet
            secondary label, so names are introduced without being the point.
            Rows come straight from /api/onboarding/app-preview (the backend's
            canonical prospective projection); an app's description stays one
            hover / focus / tap away.
          */}
          <div className={`lg:col-span-1 order-last ${selectedOutcomes.length === 0 ? 'hidden lg:block' : ''}`}>
            <div
              className="sticky top-4 overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-900 p-4 lg:p-5 text-white shadow-xl shadow-emerald-900/10"
              data-testid="setup-panel"
            >
              <h2 className="text-sm font-semibold leading-snug text-white sm:text-base">
                Here's what we'll set up for you
              </h2>
              {selectedOutcomes.length === 0 ? (
                <div className="text-center py-8 text-slate-300">
                  <svg
                    className="w-12 h-12 mx-auto mb-3 opacity-50"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                  <p className="text-sm">Pick what matters to you — we'll show what you'll get.</p>
                </div>
              ) : (
                <>
                  <ul
                    id="outcome-preview-apps"
                    className="mt-3 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:mt-4 lg:grid-cols-1"
                  >
                    {shownApps.map((app) => {
                      const benefit = appBenefit(app, mode);
                      return (
                        <li
                          key={app.appId}
                          className="flex items-start gap-2.5"
                          data-testid={`preview-benefit-${app.appId}`}
                        >
                          <AppLogo
                            appId={app.appId}
                            displayName={app.displayName}
                            iconUrl={app.iconUrl}
                            size="xs"
                            className="mt-px"
                          />
                          <AppInfoTooltip
                            description={app.description}
                            className={`block min-w-0 text-left leading-snug ${
                              app.description
                                ? 'rounded focus:outline-none focus:ring-2 focus:ring-emerald-400'
                                : ''
                            }`}
                            testId={`preview-app-${app.appId}`}
                          >
                            {benefit && (
                              <span
                                className="block text-[13px] font-medium text-white"
                                data-testid={`preview-benefit-text-${app.appId}`}
                              >
                                {benefit}
                              </span>
                            )}
                            <span
                              className="mt-0.5 block text-[11px] text-slate-400"
                              data-testid={`preview-app-name-${app.appId}`}
                            >
                              {app.displayName}
                            </span>
                          </AppInfoTooltip>
                        </li>
                      );
                    })}
                  </ul>
                  {appsCollapsible && (
                    <button
                      type="button"
                      onClick={() => setAppsExpanded((v) => !v)}
                      aria-expanded={appsExpanded}
                      aria-controls="outcome-preview-apps"
                      data-testid="preview-apps-more"
                      className="mt-2 flex w-full items-center justify-between gap-2 rounded-xl px-2.5 py-2 text-left text-xs font-semibold text-emerald-200 transition-colors hover:bg-white/[0.07] focus:outline-none focus:ring-2 focus:ring-emerald-400"
                    >
                      {appsExpanded ? 'Show less' : `Show ${selectedApps.length - shownApps.length} more`}
                      <Chevron expanded={appsExpanded} className="text-emerald-300" />
                    </button>
                  )}

                  {/* Selected outcomes */}
                  <div className="mt-4 border-t border-white/10 pt-3 lg:mt-6 lg:pt-5">
                    <h3 className="mb-0 text-xs font-semibold uppercase tracking-[0.18em] text-emerald-300 sm:mb-3">
                      Selected ({selectedOutcomes.length})
                    </h3>
                    <ul className="hidden flex-wrap gap-1.5 sm:flex">
                      {selectedOutcomes.map((code) => {
                        const outcome = outcomes.find((o) => o.code === code);
                        return (
                          <li
                            key={code}
                            className="inline-flex items-center gap-1.5 rounded-full bg-white/10 py-1 pl-1.5 pr-2.5 text-xs font-medium text-slate-100 ring-1 ring-white/10"
                          >
                            <svg
                              className="h-3.5 w-3.5 flex-shrink-0 text-emerald-300"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                            {outcome?.displayName || code}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className="mt-6 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="sticky bottom-0 z-10 -mx-3.5 sm:-mx-8 -mb-3.5 sm:-mb-8 mt-5 sm:mt-8 px-3 sm:px-8 py-2 sm:py-4 bg-white border-t border-gray-100 rounded-b-3xl flex flex-row items-center justify-between gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="px-6 py-2.5 text-gray-600 hover:text-gray-900 transition-colors font-medium"
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleContinue}
            disabled={selectedOutcomes.length === 0 || isSaving}
            className="px-5 py-2.5 sm:px-7 sm:py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg shadow-emerald-600/25"
          >
            {isSaving ? 'Saving...' : 'Continue'}
          </button>
        </div>
      </div>
    </OnboardingLayout>
  );
}
